import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ConfirmModal from '../components/ConfirmModal';
import VendorTransactionModal from '../components/VendorTransactionModal';
import { vendorAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiUsers, FiPlus, FiEdit2, FiTrash2, FiPhone, FiFileText, FiEye, FiDollarSign, FiMoreVertical } from 'react-icons/fi';
import { formatAmount } from '../utils/currency';
import { useTone } from '../contexts/ToneContext';
import './Pages.css';

const Vendors = () => {
  const { t, toneEnabled } = useTone();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTxnModal, setShowTxnModal] = useState(false);
  const [selectedVendorForTxn, setSelectedVendorForTxn] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    notes: '',
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await vendorAPI.getAll();
      setVendors(response.data.data.vendors || []);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }

    try {
      if (editingId) {
        await vendorAPI.update(editingId, formData);
        toast.success('Vendor updated');
      } else {
        await vendorAPI.create(formData);
        toast.success('Vendor created');
      }
      setShowModal(false);
      resetForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (vendor) => {
    setFormData({
      name: vendor.name || '',
      phone: vendor.phone || '',
      notes: vendor.notes || '',
    });
    setEditingId(vendor.id);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await vendorAPI.delete(deleteTarget);
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    } finally {
      setDeleteTarget(null);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', phone: '', notes: '' });
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openTxnModal = (vendorId = null) => {
    setSelectedVendorForTxn(vendorId);
    setShowTxnModal(true);
  };

  const closeTxnModal = () => {
    setShowTxnModal(false);
    setSelectedVendorForTxn(null);
  };

  const handleTxnSuccess = () => {
    fetchVendors(); // Refresh vendor data to show updated balances
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner message={t('common.loading')} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1><FiUsers style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('vendors.title')}</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => openTxnModal()} disabled={vendors.length === 0}>
              <FiDollarSign style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {toneEnabled ? 'Lenden Jodo' : 'Transaction'}
            </button>
            <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
              <FiPlus style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {t('vendors.add')}
            </button>
          </div>
        </div>

        {vendors.length === 0 ? (
          <EmptyState
            icon={<FiUsers />}
            title={t('vendors.no_vendors')}
            message={t('vendors.create_first_msg')}
            actionLabel={t('vendors.add')}
            onAction={() => { resetForm(); setShowModal(true); }}
          />
        ) : (
          <div className="grid grid-3">
            {vendors.map((vendor) => {
              // VENDOR ACCOUNTING: Same logic as Vendor Detail page
              const netBalance = parseFloat(vendor.net_balance || 0);
              const totalPaid = parseFloat(vendor.total_paid || 0);
              const totalReceived = parseFloat(vendor.total_received || 0);
              const isMenuOpen = openMenuId === vendor.id;

              // Determine net outstanding display
              // netBalance > 0: Vendor owes you (Lena Hai)
              // netBalance < 0: You owe vendor (Dena Hai)
              const getNetOutstandingDisplay = () => {
                if (netBalance > 0) {
                  return {
                    amount: formatAmount(netBalance),
                    label: toneEnabled ? 'Lena Hai' : 'Receivable',
                    color: '#27AE60', // Green - vendor owes you
                  };
                } else if (netBalance < 0) {
                  return {
                    amount: formatAmount(Math.abs(netBalance)),
                    label: toneEnabled ? 'Dena Hai' : 'Payable',
                    color: '#F39C12', // Orange - you owe vendor
                  };
                } else {
                  return {
                    amount: formatAmount(0),
                    label: toneEnabled ? 'Hisab Barabar' : 'Settled',
                    color: '#7F8C8D', // Gray - settled
                  };
                }
              };

              const netDisplay = getNetOutstandingDisplay();

              return (
                <div key={vendor.id} className="card vendor-card">
                  <div className="vendor-header">
                    <h3 style={{ cursor: 'pointer' }} onClick={() => navigate(`/vendors/${vendor.id}`)}>{vendor.name}</h3>
                    <div className="vendor-actions">
                      {/* Primary action: View - always visible */}
                      <button className="btn-icon" onClick={() => navigate(`/vendors/${vendor.id}`)} title={toneEnabled ? 'Dekho' : 'View Details'}>
                        <FiEye />
                      </button>
                      {/* Secondary actions in dropdown menu */}
                      <div className="ledger-actions-menu" ref={isMenuOpen ? menuRef : null}>
                        <button
                          className="btn-icon"
                          onClick={() => setOpenMenuId(isMenuOpen ? null : vendor.id)}
                          title={toneEnabled ? 'Aur options' : 'More options'}
                        >
                          <FiMoreVertical />
                        </button>
                        {isMenuOpen && (
                          <div className="ledger-dropdown-menu">
                            <button onClick={() => { openTxnModal(vendor.id); setOpenMenuId(null); }}>
                              <FiDollarSign size={14} />
                              <span>{toneEnabled ? 'Lenden jodo' : 'Add Transaction'}</span>
                            </button>
                            <button onClick={() => { handleEdit(vendor); setOpenMenuId(null); }}>
                              <FiEdit2 size={14} />
                              <span>{t('action.edit')}</span>
                            </button>
                            <button onClick={() => { setDeleteTarget(vendor.id); setOpenMenuId(null); }} style={{ color: '#E74C3C' }}>
                              <FiTrash2 size={14} />
                              <span>{t('action.delete')}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {vendor.phone && (
                    <div className="vendor-meta">
                      <FiPhone size={14} style={{ marginRight: 6 }} />
                      {vendor.phone}
                    </div>
                  )}

                  {vendor.notes && (
                    <div className="vendor-meta">
                      <FiFileText size={14} style={{ marginRight: 6 }} />
                      {vendor.notes.length > 50 ? vendor.notes.substring(0, 50) + '...' : vendor.notes}
                    </div>
                  )}

                  {/* Net Outstanding (Primary - Big) */}
                  <div className="vendor-balance">
                    <div style={{
                      textAlign: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid #eee',
                      marginBottom: 8,
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: netDisplay.color }}>
                        {netDisplay.amount}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: netDisplay.color, fontWeight: 500 }}>
                        {netDisplay.label}
                      </div>
                    </div>

                    {/* Secondary (Small helper text) */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#7F8C8D' }}>
                      <span>{toneEnabled ? 'Diya:' : 'Paid:'} {formatAmount(totalPaid)}</span>
                      <span>{toneEnabled ? 'Aaya:' : 'Received:'} {formatAmount(totalReceived)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showModal && (
          <div className="modal" onClick={() => { setShowModal(false); resetForm(); }}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2>{editingId ? t('vendors.edit') : t('vendors.add')}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('vendors.name')} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Ali Electronics"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('vendors.phone')} ({t('common.optional')})</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="e.g. 0300-1234567"
                  />
                </div>
                <div className="form-group">
                  <label>{t('vendors.notes')} ({t('common.optional')})</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Additional notes about this vendor"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                    {t('action.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingId ? t('action.update') : t('action.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteTarget && (
          <ConfirmModal
            title={t('vendors.delete')}
            message={t('vendors.delete_confirm')}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}

        {showTxnModal && (
          <VendorTransactionModal
            isOpen={showTxnModal}
            onClose={closeTxnModal}
            onSuccess={handleTxnSuccess}
            vendors={vendors}
            preSelectedVendorId={selectedVendorForTxn}
          />
        )}
      </div>
    </Layout>
  );
};

export default Vendors;
